import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, AlertCircle, CheckCircle2, Bell, Shield } from "lucide-react";
import { toast } from "sonner";
import { formatUKDate } from "@/lib/dateUtils";

interface RiskAlertsProps {
  clinicianId: string;
}

export function RiskAlerts({ clinicianId }: RiskAlertsProps) {
  const queryClient = useQueryClient();
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  // Fetch alerts for all clients of this clinician
  const { data: alerts, isLoading } = useQuery({
    queryKey: ['risk-alerts', clinicianId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_risk_alerts')
        .select('*')
        .eq('clinician_id', clinicianId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Acknowledge alert
  const acknowledgeMutation = useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase
        .from('client_risk_alerts')
        .update({
          status: 'acknowledged',
          acknowledged_at: new Date().toISOString()
        })
        .eq('id', alertId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk-alerts', clinicianId] });
      toast.success("Alert acknowledged");
    }
  });

  // Resolve alert
  const resolveMutation = useMutation({
    mutationFn: async ({ alertId, notes }: { alertId: string; notes: string }) => {
      const { error } = await supabase
        .from('client_risk_alerts')
        .update({
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolution_notes: notes
        })
        .eq('id', alertId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk-alerts', clinicianId] });
      setResolvingId(null);
      setResolutionNotes('');
      toast.success("Alert resolved");
    }
  });

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'high':
        return <AlertCircle className="h-5 w-5 text-orange-600" />;
      case 'medium':
        return <Bell className="h-5 w-5 text-yellow-600" />;
      default:
        return <Shield className="h-5 w-5 text-blue-600" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge className="bg-red-600/20 text-red-700 border-red-600/30">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-500/20 text-orange-700 border-orange-500/30">High</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500/20 text-yellow-700 border-yellow-500/30">Medium</Badge>;
      default:
        return <Badge className="bg-blue-500/20 text-blue-700 border-blue-500/30">Low</Badge>;
    }
  };

  const getAlertTypeLabel = (type: string) => {
    switch (type) {
      case 'mood_drop': return 'Mood Deterioration';
      case 'crisis_keyword': return 'Crisis Keywords Detected';
      case 'sleep_deterioration': return 'Sleep Pattern Decline';
      case 'missed_checkins': return 'Missed Check-ins';
      case 'assessment_threshold': return 'Assessment Threshold';
      case 'manual': return 'Manual Alert';
      default: return type;
    }
  };

  const activeAlerts = alerts?.filter(a => a.status === 'active') || [];
  const resolvedAlerts = alerts?.filter(a => a.status !== 'active') || [];

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            Risk Alerts
          </CardTitle>
          {activeAlerts.length > 0 && (
            <Badge className="bg-red-500/20 text-red-700 border-red-500/30">
              {activeAlerts.length} Active
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading alerts...</p>
        ) : alerts?.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500/50 mb-3" />
            <p className="text-muted-foreground">No risk alerts</p>
            <p className="text-sm text-muted-foreground">
              Client data is within expected parameters
            </p>
          </div>
        ) : (
          <>
            {/* Active Alerts */}
            {activeAlerts.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-red-600">Active Alerts</h4>
                {activeAlerts.map(alert => (
                  <div
                    key={alert.id}
                    className={`p-4 rounded-lg border ${
                      alert.severity === 'critical' 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : alert.severity === 'high'
                        ? 'bg-orange-500/10 border-orange-500/30'
                        : 'bg-yellow-500/10 border-yellow-500/30'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getSeverityIcon(alert.severity)}
                        <div>
                          <p className="font-medium">{getAlertTypeLabel(alert.alert_type)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatUKDate(new Date(alert.created_at))}
                          </p>
                        </div>
                      </div>
                      {getSeverityBadge(alert.severity)}
                    </div>

                    <p className="text-sm mb-3">{alert.description}</p>

                    {alert.recommended_action && (
                      <div className="p-2 rounded bg-background/50 mb-3">
                        <p className="text-xs font-medium mb-1">Recommended Action:</p>
                        <p className="text-sm text-muted-foreground">{alert.recommended_action}</p>
                      </div>
                    )}

                    {resolvingId === alert.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={resolutionNotes}
                          onChange={(e) => setResolutionNotes(e.target.value)}
                          placeholder="Resolution notes..."
                          className="bg-background/50"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setResolvingId(null);
                              setResolutionNotes('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => resolveMutation.mutate({ alertId: alert.id, notes: resolutionNotes })}
                            disabled={resolveMutation.isPending}
                          >
                            Resolve Alert
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        {alert.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeMutation.mutate(alert.id)}
                            disabled={acknowledgeMutation.isPending}
                          >
                            Acknowledge
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => setResolvingId(alert.id)}
                        >
                          Resolve
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Resolved Alerts */}
            {resolvedAlerts.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground">Resolved Alerts</h4>
                {resolvedAlerts.slice(0, 5).map(alert => (
                  <div
                    key={alert.id}
                    className="p-3 rounded-lg bg-muted/30 border border-border/30"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span className="text-sm">{getAlertTypeLabel(alert.alert_type)}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatUKDate(new Date(alert.resolved_at || alert.created_at))}
                      </span>
                    </div>
                    {alert.resolution_notes && (
                      <p className="text-xs text-muted-foreground mt-1 ml-6">
                        {alert.resolution_notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
